import { FCC } from "@/types/common";
import { Grid, Stack, Typography } from "@mui/material";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { RsoBasicInfoSection } from "@/features/rso/components/rso-form/RsoBasicInfoSection";
import { RsoIndustrySection } from "@/features/rso/components/rso-form/RsoIndustrySection";
import { RsoContractInfoSection } from "@/features/rso/components/rso-detail/RsoContractInfoSection";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { AppRoutes } from "@/routes/config";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { text } from "@/theme/colors";
import { IconSave } from "@/assets/icons";
import { TRsoForm } from "@/features/rso/types/rsoForm";
import { TGetRSODto } from "@/api/services/main/rso/dtos/get-rso.dto";
import { Form } from "@/components/elements/Form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { queryClient } from "@/constants/queryClient";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { updateRsoApi } from "@/api/services/main/rso";
import { convertRsoFormDataReq } from "@/features/rso/utils/rsoConverts";
import { rsoSchema } from "@/features/rso/schema/rsoSchema";

type TRsoFormContentProps = {
  defaultValues: Partial<TRsoForm>;
  rsoData: TGetRSODto;
};

export const RsoForm: FCC<TRsoFormContentProps> = ({ defaultValues, rsoData }) => {
  const navigate = useNavigate();
  const [isDisableButton, setIsDisableButton] = useState(false);
  const methods = useForm<TRsoForm>({
    defaultValues: {
      ...defaultValues,
    },
    resolver: yupResolver(rsoSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const tabs = useMemo(() => {
    return [
      {
        title: "基本情報",
        content: <RsoBasicInfoSection />,
      },
      {
        title: "取り扱う分野",
        content: <RsoIndustrySection />,
      },
      {
        title: "契約情報",
        content: <RsoContractInfoSection data={rsoData} />,
      },
    ];
  }, [defaultValues, rsoData]);

  const handleSaveData = async (data: TRsoForm) => {
    setIsDisableButton(true);
    try {
      const payload = convertRsoFormDataReq(data);
      const rs = await updateRsoApi(payload);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(AppRoutes.rsoDetail);
    } catch (e) {
      showError(e);
    } finally {
      setIsDisableButton(false);
    }
  };

  return (
    <Form methods={methods} onSubmit={handleSaveData} schema={rsoSchema} initialValues={defaultValues}>
      <Stack gap={2}>
        <Typography variant="head20Bold" color={text.primary}>
          登録支援機関情報
        </Typography>
      </Stack>
      <Grid container sx={{ marginTop: 4 }}>
        <Grid item xs={12}>
          <MenuNavigate
            contents={tabs}
            contentAffix={
              <FooterContainer>
                <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Button variant="ghost" size="sm" onClick={() =>{ navigate(AppRoutes.rsoDetail); }} sx={{ width: "60px" }}>
                    戻る
                  </Button>
                  <Stack direction="row" gap={3}>
                    <Button type="submit" variant="primary" size="sm" startIcon={<IconSave />} sx={{ fontWeight: 500, width: "88px" }} disabled={isDisableButton}>
                      保存
                    </Button>
                  </Stack>
                </Stack>
              </FooterContainer>
            }
            contentWidth="900px"
          />
        </Grid>
      </Grid>
    </Form>
  );
};
